import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, CalendarDaysIcon, TrashIcon, TriangleAlertIcon, UsersIcon } from 'lucide-react';
import { notices, type NoticeCategory } from '../../data/avis';
interface BellPreviewProps {
  onNavigate: () => void;
}
const categoryIcons: Record<NoticeCategory, typeof BellIcon> = {
  urgent: TriangleAlertIcon,
  service: TrashIcon,
  loisirs: UsersIcon,
  personnalise: CalendarDaysIcon,
  municipal: BellIcon
};
export function BellPreview({
  onNavigate
}: BellPreviewProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [exitX, setExitX] = useState<Record<string, number>>({});
  const visible = notices.filter(notice => notice.unread || notice.category === 'urgent').filter(notice => !dismissed.includes(notice.id)).slice(0, 3);
  const dismiss = (id: string, direction: number) => {
    setExitX(map => ({
      ...map,
      [id]: direction
    }));
    setDismissed(list => [...list, id]);
  };
  const open = (id: string) => {
    dismiss(id, 1);
    window.setTimeout(() => {
      onNavigate();
      navigate('/notifications');
    }, 420);
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.92
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.92
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    originX: 1,
    originY: 0
  }} className="absolute right-0 top-full w-[340px] pt-3" data-id="element-361" data-mp="21j16s6ro6707">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10" data-id="element-362" data-mp="2kfsle2w9nxxx">
        <p className="px-5 pb-1 pt-4 text-sm font-bold text-ville-navy" data-id="element-363" data-mp="2fgz8abkw68ya">Notifications</p>

        <ul className="overflow-hidden" data-id="element-364" data-mp="q1rklch5p0v7">
          <AnimatePresence initial={false} data-id="element-365" data-mp="q2km9z19d06h">
            {visible.map(notice => {
            const Icon = categoryIcons[notice.category];
            const urgent = notice.category === 'urgent';
            return <motion.li key={notice.id} layout drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.5} onDragEnd={(_, info) => {
              if (Math.abs(info.offset.x) > 80) {
                dismiss(notice.id, info.offset.x > 0 ? 1 : -1);
              }
            }} exit={{
              x: (exitX[notice.id] ?? 1) * 320,
              opacity: 0,
              transition: {
                duration: 0.42,
                ease: [0.23, 1, 0.32, 1]
              }
            }} transition={{
              duration: 0.42,
              ease: [0.23, 1, 0.32, 1]
            }} className="cursor-pointer touch-pan-y bg-white" data-id="element-366" data-mp="1asn7ymkitrhg">
                  <button type="button" onClick={() => open(notice.id)} className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors duration-150 ease-ville hover:bg-slate-50" data-id="element-367" data-mp="2ci0o9jmpi37o">
                    <span className={`grid h-10 w-10 shrink-0 place-items-center self-stretch rounded-xl ${urgent ? 'bg-red-50 text-red-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} data-id="element-368" data-mp="rsx4y9qxqje">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-369" data-mp="oiybta3yacs4" />
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-370" data-mp="jisepeewvy2n">
                      <span className="flex items-baseline justify-between gap-3" data-id="element-371" data-mp="jlewe6vintyl">
                        <span className="text-sm font-bold text-ville-navy" data-id="element-372" data-mp="er0x3tdqh2on">{notice.title}</span>
                        <span className="shrink-0 text-[11px] font-semibold text-slate-400" data-id="element-373" data-mp="9szch40nsvb2">{notice.ago}</span>
                      </span>
                      <span className="mt-1 block truncate text-xs text-slate-500" data-id="element-374" data-mp="12i6cy8jobuyy">{notice.description}</span>
                    </span>
                  </button>
                </motion.li>;
          })}
          </AnimatePresence>
        </ul>

        {visible.length === 0 && <p className="px-5 py-4 text-sm text-slate-500" data-id="element-375" data-mp="rwwvixi8zwiz">Aucune nouvelle notification.</p>}

        <div className="px-5 pb-4 pt-1" data-id="element-376" data-mp="i13ild0ma10o">
          <button type="button" onClick={() => {
          onNavigate();
          navigate('/notifications');
        }} className="block w-full rounded-full px-4 py-2 text-center text-sm font-bold text-ville-indigo transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft" data-id="element-377" data-mp="i24t9gdq0lhv">
            Voir plus
          </button>
        </div>
      </div>
    </motion.div>;
}